import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    category: "Campamentos de Verano",
    color: "#2B87BE",
    bg: "#EBF5FB",
    logo: "/images/logos/campamento-dacpac.png",
    items: [
      {
        q: "¿Es necesario un diagnóstico oficial de alta capacidad?",
        a: "Sí. Para inscribirse en el campamento es necesario contar con un diagnóstico oficial de alta capacidad, emitido por un profesional cualificado (psicólogo, psicopedagogo o equipo de orientación educativa). Si tienes dudas sobre qué tipo de evaluación es válida, contáctanos."
      },
      {
        q: "¿El campamento es solo para niños con dificultades?",
        a: "No. Es para niños y jóvenes con alta capacidad diagnosticada que pueden estar teniendo dificultades de adaptación o simplemente necesitan un entorno más estimulante. No es un programa terapéutico ni remedial: es una experiencia de crecimiento y conexión."
      },
      {
        q: "¿Pueden participar jóvenes con doble excepcionalidad (TDAH, TEA...)?",
        a: "Sí, siempre que cuenten con diagnóstico oficial de alta capacidad y el equipo considere que el programa es adecuado. Te pedimos que nos cuentes la situación específica al solicitar la preinscripción."
      },
      {
        q: "¿Cuál es el ratio de monitores por participantes?",
        a: "Trabajamos con un máximo de 5 participantes por monitor. Este ratio bajo es fundamental para garantizar la atención personalizada que requiere la alta capacidad."
      },
      {
        q: "¿Cómo se gestionan los conflictos entre participantes?",
        a: "Nuestro equipo tiene formación específica en gestión emocional y mediación. Los conflictos se abordan como oportunidades de aprendizaje, con un protocolo definido y un ambiente de cohesión desde el primer día."
      },
      {
        q: "¿Cuándo se publicará la próxima edición?",
        a: "Estamos preparando la próxima temporada. Publicaremos fechas, ubicación, programa, precio y condiciones cuando estén cerrados."
      },
      {
        q: "¿Cómo puedo recibir la información cuando se publique?",
        a: "Puedes dejar tus datos en el formulario de aviso de la página Campamento de Verano. Te escribiremos cuando la próxima edición esté disponible."
      },
      {
        q: "¿Ya se puede reservar plaza?",
        a: "No. Ahora mismo el campamento está en fase informativa. Abriremos reservas cuando la próxima edición esté publicada."
      },
      {
        q: "¿Se puede repetir de un año para otro?",
        a: "Sí, y muchos participantes repiten. El programa evoluciona cada año, por lo que la experiencia siempre es renovada."
      },
    ]
  },
  {
    category: "Encuentro Adultos",
    color: "#2D6A4F",
    bg: "#E8F4EC",
    logo: "/images/logos/encuentro-dacpac.png",
    items: [
      {
        q: "¿Es necesario diagnóstico oficial para participar?",
        a: "No. Para participar en el Encuentro Adultos no es necesario aportar informe de evaluación. Si tienes dudas sobre si este espacio es para ti, escríbenos y te orientamos."
      },
      {
        q: "¿Qué incluye el Encuentro?",
        a: "Alojamiento en La Lobera de Gredos, manutención durante los tres días, todas las dinámicas y actividades del programa, y acceso a la comunidad DAC-PAC posterior al encuentro. El precio estará disponible próximamente."
      },
      {
        q: "¿Para quién está diseñado el Encuentro?",
        a: "Para adultos con alta capacidad que buscan conexión entre iguales, conversaciones profundas y un espacio donde no necesiten explicarse. Si llevas tiempo sintiéndote diferente y nunca has encontrado tu comunidad, este encuentro es para ti."
      },
      {
        q: "¿Puedo asistir si es la primera vez que participo en algo de DAC-PAC?",
        a: "Por supuesto. El Encuentro está diseñado para ser acogedor también para quienes llegan por primera vez. El equipo se encarga de que nadie se sienta fuera de lugar desde el primer momento."
      },
      {
        q: "¿Cuándo y dónde se celebra?",
        a: "Estamos cerrando el programa para 2026. Muy pronto compartiremos las fechas definitivas y todos los detalles del encuentro."
      },
      {
        q: "¿Cómo me apunto a la lista de interés?",
        a: "Puedes rellenar el formulario de la página Encuentro Adultos o escribirnos directamente a info@dacpac.es. Te avisaremos en cuanto se abran las inscripciones."
      },
    ]
  },
  {
    category: "Extraescolares",
    color: "#C9974A",
    bg: "#FBF3E3",
    logo: "/images/logos/cursos-dacpac.png",
    items: [
      {
        q: "¿Qué son las Extraescolares DAC-PAC?",
        a: "Son actividades semanales pensadas para niños, niñas y jóvenes con curiosidad intensa, ganas de aprender y necesidad de reto. No son clases de refuerzo: son espacios de aprendizaje activo, con grupos reducidos y contenido trabajado con más recorrido."
      },
      {
        q: "¿A quién van dirigidos?",
        a: "A niños, niñas y jóvenes interesados en profundizar de verdad en un área de conocimiento. Cada extraescolar especificará el perfil y la edad recomendados, pero no es necesario contar con una evaluación de alta capacidad."
      },
      {
        q: "¿Es necesario diagnóstico oficial para las extraescolares?",
        a: "No. Para las extraescolares no es necesario aportar informe de evaluación. Si quieres saber si una actividad encaja con tu hijo o hija, puedes escribirnos."
      },
      {
        q: "¿Cuándo empiezan las extraescolares?",
        a: "Las extraescolares están previstas para el curso 2026/2027, con inicio en septiembre y duración hasta junio. Puedes solicitar información o iniciar la inscripción desde la página de extraescolares."
      },
      {
        q: "¿Cuántas plazas hay por grupo?",
        a: "Las extraescolares se organizan en grupos reducidos para garantizar una atención cercana, participación activa y profundidad real en cada sesión."
      },
      {
        q: "¿Cómo puedo solicitar información?",
        a: "Rellena el formulario de la página Extraescolares o escríbenos a info@dacpac.es indicando el área de interés y el perfil del participante."
      },
    ]
  },
];

function FAQItem({ item, index, accentColor }) {
  const [open, setOpen] = useState(false);

  return (
    <div data-testid={`faq-item-${index}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between py-4 text-left gap-3"
        data-testid={`faq-toggle-${index}`}
      >
        <span style={{ fontFamily: "Manrope, sans-serif", fontWeight: 600, color: "#0D3A4F", fontSize: "0.85rem", lineHeight: 1.5 }}>
          {item.q}
        </span>
        <ChevronDown
          size={15}
          style={{
            color: accentColor,
            flexShrink: 0,
            marginTop: "3px",
            transition: "transform 0.3s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)"
          }}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28 }}
            style={{ overflow: "hidden" }}
          >
            <p className="pb-4 text-slate-500 text-xs leading-relaxed" style={{ fontFamily: "Manrope, sans-serif" }}>
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  let globalIndex = 0;

  return (
    <main data-testid="faq-page" style={{ fontFamily: "Manrope, sans-serif" }}>
      <section className="relative pt-32 pb-16 px-5 lg:px-8" style={{ background: "#F8FAFC" }}>
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="section-label mb-5">Preguntas frecuentes</div>
            <h1
              style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F", lineHeight: 1.2 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Todo lo que necesitas saber<br />
              <em style={{ color: "#1B5F7E" }}>antes de apuntarte.</em>
            </h1>
            <p className="text-slate-500 leading-relaxed">
              Preguntas organizadas por experiencia. Si no encuentras respuesta, escríbenos a{" "}
              <a href="mailto:info@dacpac.es" className="text-[#1B5F7E] font-medium hover:underline">
                info@dacpac.es
              </a>
            </p>
            <p className="text-slate-500 leading-relaxed mt-4">
              Esta pagina responde a las dudas mas comunes sobre el campamento para altas capacidades, el encuentro para adultos y las extraescolares de DAC-PAC.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 pb-28 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {faqs.map((cat, ci) => (
              <motion.div
                key={ci}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: ci * 0.1 }}
                className="rounded-2xl overflow-hidden border"
                style={{ borderColor: cat.color + "30" }}
                data-testid={`faq-category-${ci}`}
              >
                <div
                  className="px-6 py-5 flex items-center gap-3"
                  style={{ background: cat.bg, borderBottom: `2px solid ${cat.color}` }}
                >
                  <img src={cat.logo} alt={cat.category} className="h-7 w-7 object-contain flex-shrink-0" />
                  <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F", fontSize: "1rem", fontWeight: 700 }}>
                    {cat.category}
                  </h3>
                  <span
                    className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ background: cat.color + "18", color: cat.color, fontFamily: "Manrope, sans-serif" }}
                  >
                    {cat.items.length}
                  </span>
                </div>
                <div className="bg-white px-6 pt-1 pb-2">
                  {cat.items.map((item) => {
                    const idx = globalIndex++;
                    return <FAQItem key={idx} item={item} index={idx} accentColor={cat.color} />;
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6"
            style={{ background: "#F0F9FF", border: "1px solid #D0E8F5" }}
          >
            <div>
              <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-xl font-semibold mb-2">
                ¿Tienes mas dudas?
              </h3>
              <p className="text-slate-500 text-sm">
                Escribenos y te respondemos en menos de 24 horas. Siempre hay una persona real al otro lado.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 flex-shrink-0">
              <Link to="/contacto" data-testid="faq-contacto-cta" className="btn-primary" style={{ fontSize: "0.85rem", padding: "0.6rem 1.4rem" }}>
                Contactar
              </Link>
              <Link to="/preinscripcion" className="btn-outline" style={{ fontSize: "0.85rem", padding: "0.6rem 1.4rem" }}>
                Preinscripcion
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
